import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertTriangle, AlertOctagon, Loader2, Info } from "lucide-react";

/**
 * Small pill badge that surfaces the Vision auto-validate result for a
 * single generated image. Visible to admin / sales only — the parent
 * component is responsible for the role gate.
 *
 * Three visual states:
 *   • SCORE >= 80  → emerald "✓ {score}" with no detail (clean render)
 *   • 50 <= SCORE < 80 → amber "⚠ {score}" with click-to-expand details
 *   • SCORE < 50  → rose "⨉ {score}" with click-to-expand details
 *   • SCORE = null + skipped → grey "—" (no validation possible)
 *   • SCORE = null + error → grey "?" with the error in tooltip
 *
 * On click → opens a small popover under the badge listing missing /
 * unexpected categories + the Romanian observation.
 */
export default function QualityBadge({ check, slugNames = {} }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!check) return null;

  const { score, missing_categories, unexpected_categories, observation, error, skipped } = check;

  if (skipped) {
    return (
      <span
        className="px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold bg-white/5 text-zinc-500 border border-white/10 inline-flex items-center gap-1"
        title={t("detail.quality_skipped_title")}
        data-testid="quality-badge-skipped"
      >
        <Info size={10} /> n/a
      </span>
    );
  }
  if (score === null || score === undefined) {
    // Either still pending or errored — show neutral state
    return (
      <span
        className="px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold bg-zinc-700/30 text-zinc-400 border border-zinc-600 inline-flex items-center gap-1"
        title={error || t("detail.quality_pending_title")}
        data-testid="quality-badge-pending"
      >
        {error ? <Info size={10} /> : <Loader2 size={10} className="animate-spin" />}
        {error ? "?" : t("detail.quality_pending_label")}
      </span>
    );
  }

  const tone =
    score >= 80
      ? { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", icon: CheckCircle2 }
      : score >= 50
      ? { cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", icon: AlertTriangle }
      : { cls: "bg-rose-500/15 text-rose-300 border-rose-500/40", icon: AlertOctagon };
  const Icon = tone.icon;

  const hasDetail = (missing_categories?.length || 0) + (unexpected_categories?.length || 0) > 0 || observation;

  const slugName = (s) => slugNames[s] || s;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold border inline-flex items-center gap-1 transition ${tone.cls} ${
          hasDetail ? "hover:brightness-125 cursor-pointer" : "cursor-default"
        }`}
        title={t("detail.quality_check_title")}
        data-testid="quality-badge"
      >
        <Icon size={10} /> {score}/100
      </button>

      {open && hasDetail && (
        <div
          className="absolute z-30 top-full left-0 mt-1 w-72 rounded-md border border-amber-500/30 bg-zinc-950 shadow-2xl p-3"
          data-testid="quality-badge-popover"
        >
          {observation && (
            <p className="text-xs text-zinc-300 italic mb-2 leading-relaxed">
              „{observation}"
            </p>
          )}
          {missing_categories?.length > 0 && (
            <div className="mb-2">
              <div className="text-[9px] uppercase tracking-wider text-rose-400 mb-1">
                {t("detail.quality_missing_label", { count: missing_categories.length })}
              </div>
              <div className="flex flex-wrap gap-1">
                {missing_categories.map((s) => (
                  <span
                    key={s}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30"
                  >
                    {slugName(s)}
                  </span>
                ))}
              </div>
            </div>
          )}
          {unexpected_categories?.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-amber-400 mb-1">
                {t("detail.quality_unexpected_label", { count: unexpected_categories.length })}
              </div>
              <div className="flex flex-wrap gap-1">
                {unexpected_categories.map((s) => (
                  <span
                    key={s}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                  >
                    {slugName(s)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
